import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { CapacityConsole } from "./CapacityConsole";
import { BatteryConsole } from "./BatteryConsole";
import { CapacityRow } from "../../PowerProcurement/AddOffer";
import { BatteryRow } from "../../PowerProcurement/AddBattery";
import {
  AddCapacityData,
  AddBatteryData,
  getSurplusOptimizerBatteryData,
  getSurplusOptimizerCapacityData,
  postBessPageInfo,
  postBessMakeScenario,
  postBessSwitchScenario,
  getSurplusharayanaSession,
  getBessTaskStatus,
} from "../../../../../Rest_api/RestApi";

const POLL_INTERVAL_MS = 5000;
const STATUS_PROCESSING = 3;
const STATUS_COMPLETED = 1;

interface CapacityOffer {
  start_date: string;
  end_date: string;
  source_type: string;
  id: number;
  source_id: number;
  plant_capacity: string;
  plf: string;
  variable_charge: string;
  fixed_charge: string;
  status?: boolean;
  offerType: "capacity";
}

interface BatteryOffer {
  start_date: string;
  end_date: string;
  id: number;
  battery_id: number;
  battery_capacity_in_mwh: number;
  chargingRate: string;
  vc: string;
  fc: string;
  status?: boolean;
  offerType: "battery";
}

type CombinedOffer = CapacityOffer | BatteryOffer;

interface PPEFormData {
  startDate: string;
  endDate: string;
  scenario: string;
  offers: CombinedOffer[];
}

interface PPEConsoleProps {
  onApply: (data: PPEFormData) => void;
  onReset: () => void;
  onRefreshAllComponents?: () => void;
  loading: boolean;
  refreshKey: any;
  isSliderOpen: boolean;
  setRefreshKey: any;
  onActiveScenarioChange?: (scenario: number) => void;
}

export default function PPEConsole({
  onApply,
  onReset,
  onRefreshAllComponents,
  refreshKey,
  loading: applyLoading,
  isSliderOpen,
  setRefreshKey,
  onActiveScenarioChange,
}: PPEConsoleProps) {
  const [offers, setOffers] = useState<CombinedOffer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scenario, setScenario] = useState<string>("1");
  const [availableScenarios, setAvailableScenarios] = useState<number[]>([]);
  const [activeScenario, setActiveScenario] = useState<number>(0);
  const [makeScenarioLoading, setMakeScenarioLoading] = useState(false);
  const [isApplied, setIsApplied] = useState(false);
  const [isFinalized, setIsFinalized] = useState(false);
  const [processing, setProcessing] = useState(false);
  const pollIntervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = () => {
    if (pollIntervalRef.current !== null) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const SelectedStartDate = useSelector(
    (state: any) => state.ProcurementOptimizerDemoDate.startDate
  );
  const SelectedEndDate = useSelector(
    (state: any) => state.ProcurementOptimizerDemoDate.endDate
  );

  const fetchScenarios = useCallback(async () => {
    try {
      const response = await getSurplusharayanaSession();
      if (response?.scenarios) {
        const scenarios = response.scenarios
          .map((s: any) => s.scenario_number)
          .sort((a: number, b: number) => a - b);
        setAvailableScenarios(scenarios);

        if (response.active_scenario?.scenario_number !== undefined) {
          setActiveScenario(response.active_scenario.scenario_number);
          setIsFinalized(response.active_scenario.is_finalized);
          onActiveScenarioChange?.(response.active_scenario.scenario_number);
        }
      }
    } catch (err) {
      console.error("Error fetching scenarios:", err);
    }
  }, []);

  const fetchOffers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [batteryData, capacityData] = await Promise.all([
        getSurplusOptimizerBatteryData(),
        getSurplusOptimizerCapacityData(),
      ]);

      const formattedBatteries: BatteryOffer[] = (
        Array.isArray(batteryData) ? batteryData : []
      ).map((battery: any) => ({
        id: battery.id,
        battery_id: battery.battery_id,
        start_date: battery.start_date || "",
        end_date: battery.end_date || "",
        chargingRate: battery.charge_discharge_rate_in_mw || "",
        battery_capacity_in_mwh: battery.battery_capacity_in_mwh || "",
        vc: battery.vc || battery.variable_charge || "",
        fc: battery.fc || battery.fixed_charge || "",
        status: battery.status ?? true,
        offerType: "battery" as const,
      }));

      const formattedCapacities: CapacityOffer[] = (
        Array.isArray(capacityData) ? capacityData : []
      ).map((capacity: any) => ({
        id: capacity.id,
        source_id: capacity.source_id,
        start_date: capacity.start_date || "",
        end_date: capacity.end_date || "",
        source_type: capacity.type || capacity.source_type || "",
        plant_capacity: capacity.plant_capacity || "",
        plf: capacity.pf || capacity.plf || "",
        variable_charge: capacity.vc || capacity.variable_charge || "",
        fixed_charge: capacity.fc || capacity.fixed_charge || "",
        status: capacity.status ?? true,
        offerType: "capacity" as const,
      }));

      setOffers([...formattedCapacities, ...formattedBatteries]);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch offers";
      setError(errorMessage);
      console.error("Error fetching offers:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOffers();
    fetchScenarios();
  }, [fetchOffers, fetchScenarios, refreshKey]);

  const handleAddCapacity = async (data: CapacityRow[]) => {
    setLoading(true);
    setError(null);
    setIsApplied(false);
    try {
      const apiData = data.map((capacity) => ({
        source_type: capacity.type,
        start_date: capacity.startDate,
        end_date: capacity.endDate,
        plant_capacity: capacity.quantum,
        plf: capacity.pf,
        variable_charge: capacity.vc,
        fixed_charge: capacity.fc,
      }));
      await AddCapacityData(apiData);
      setRefreshKey((prev: number) => prev + 1);
      await fetchOffers();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to add Capacity";
      setError(errorMessage);
      console.error("Error adding capacity:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleAddBattery = async (data: BatteryRow[]) => {
    setLoading(true);
    setError(null);
    setIsApplied(false);
    try {
      const apiData = data.map((battery) => ({
        start_date: battery.startDate,
        end_date: battery.endDate,
        battery_capacity_in_mwh: battery.quantum,
        charge_discharge_rate_in_mw: battery.chargingRate,
        variable_charge: battery.vc,
        fixed_charge: battery.fc,
      }));

      await AddBatteryData(apiData);
      setRefreshKey((prev: number) => prev + 1);
      await fetchOffers();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to add Battery";
      setError(errorMessage);
      console.error("Error adding battery:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleToggleOffer = (id: number) => {
    setOffers((prev) =>
      prev.map((offer) =>
        offer.id === id ? { ...offer, status: !offer.status } : offer
      )
    );
    setIsApplied(false);
  };

  const handleReset = () => {
    setScenario("");
    setIsApplied(false);
    fetchOffers();
    onReset();
  };

  const handleApply = async () => {
    setLoading(true);
    setError(null);
    try {
      const capacityIds = capacities.filter((c) => c.status).map((c) => c.id);
      const batteryIds = batteries.filter((b) => b.status).map((b) => b.id);

      const apiData = {
        capacity_ids: capacityIds,
        battery_ids: batteryIds,
        start_date: offers[0]?.start_date || SelectedStartDate,
        end_date: offers[0]?.end_date || SelectedEndDate,
      };

      const res = await postBessPageInfo(apiData);

      const formData: PPEFormData = {
        startDate: offers[0]?.start_date || "",
        endDate: offers[0]?.end_date || "",
        scenario: scenario,
        offers: offers,
      };

      if (res?.task_id) {
        setLoading(false);
        setProcessing(true);

        pollIntervalRef.current = setInterval(async () => {
          try {
            const taskStatus = await getBessTaskStatus(res.task_id);

            if (taskStatus?.status === STATUS_COMPLETED) {
              stopPolling();
              setProcessing(false);
              setIsApplied(true);
              onApply(formData);
            } else if (
              taskStatus?.status !== STATUS_PROCESSING &&
              taskStatus?.status !== undefined
            ) {
              stopPolling();
              setProcessing(false);
              setError(
                `Task failed with status: ${taskStatus?.status_display ?? taskStatus?.status}`
              );
            }
          } catch (pollError) {
            stopPolling();
            setProcessing(false);
            setError("Failed to check task status. Please try again.");
            console.error("Polling error:", pollError);
          }
        }, POLL_INTERVAL_MS);
      } else {
        setIsApplied(true);
        onApply(formData);
        setLoading(false);
      }
    } catch (err) {
      stopPolling();
      setProcessing(false);
      const errorMessage =
        err instanceof Error ? err.message : "Failed to apply changes";
      setError(errorMessage);
      console.error("Error applying:", err);
      setLoading(false);
    }
  };

  const handleMakeScenario = async () => {
    setMakeScenarioLoading(true);
    setError(null);
    try {
      const capacityIds = capacities.filter((c) => c.status).map((c) => c.id);
      const batteryIds = batteries.filter((b) => b.status).map((b) => b.id);

      const apiData = {
        capacity_ids: capacityIds,
        battery_ids: batteryIds,
        start_date: offers[0]?.start_date || SelectedStartDate,
        end_date: offers[0]?.end_date || SelectedEndDate,
      };

      await postBessMakeScenario(apiData);
      setRefreshKey((prev: number) => prev + 1);
      await fetchScenarios();

      onRefreshAllComponents?.();
      setIsApplied(false); // Disable Make Scenario after creating
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to create scenario";
      setError(errorMessage);
      console.error("Error creating scenario:", err);
    } finally {
      setMakeScenarioLoading(false);
    }
  };

  // Check if Make Scenario button should be disabled
  const isMakeScenarioDisabled = () => {
    // Enable Make Scenario ONLY if finalized
    return !isFinalized || makeScenarioLoading || loading;
  };

  const handleSwitchScenario = async (scenarioNumber: number) => {
    setError(null);
    try {
      await postBessSwitchScenario(scenarioNumber);
      setActiveScenario(scenarioNumber);
      onActiveScenarioChange?.(scenarioNumber);

      onRefreshAllComponents?.();
      onApply({
        startDate: offers[0]?.start_date || "",
        endDate: offers[0]?.end_date || "",
        scenario: scenarioNumber.toString(),
        offers: offers,
      });
      await fetchScenarios();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to switch scenario";
      setError(errorMessage);
      console.error("Error switching scenario:", err);
    }
  };

  const capacities = offers.filter(
    (offer) => offer.offerType === "capacity"
  ) as CapacityOffer[];
  const batteries = offers.filter(
    (offer) => offer.offerType === "battery"
  ) as BatteryOffer[];

  return (
    <div className="w-full h-full bg-[#F8F9FA] flex flex-col">
      <div className="flex-1 flex flex-col h-full">
        {availableScenarios.length > 0 && (
          <div className="px-4 pt-4 pb-4 bg-white">
            <div className="flex justify-between items-center w-full">
              <h3 className="text-sm font-medium text-[#34656d]">
                Capacity Modeling Console
              </h3>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto bg-[#F8F9FA] px-2 py-2">
          {availableScenarios.length > 0 && (
            <div className="rounded-xl border border-[#E0E0E0] bg-white mb-2">
              {/* Card Header */}
              <div className="flex items-center justify-between p-2 border-b border-[#E0E0E0]">
                <span className="text-xs text-[#6F6D7C] font-medium">Scenarios</span>
                <button
                  onClick={handleMakeScenario}
                  disabled={isMakeScenarioDisabled()}
                  className="w-6 h-6 flex items-center justify-center rounded border border-[#F1935C] text-[#F1935C] hover:bg-[#F1935C] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="text-base leading-none mb-0.5">+</span>
                </button>
              </div>

              {/* Card Body */}
              <div className="p-2">
                <div className="flex flex-wrap items-center gap-2 overflow-y-auto custom-scrollbar max-h-[90px]">
                  {availableScenarios.map((scenarioNum) => (
                    <button
                      key={scenarioNum}
                      onClick={() => !loading && handleSwitchScenario(scenarioNum)}
                      disabled={loading}
                      className={`
                        flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-200
                        ${scenarioNum === 0
                          ? "bg-[#EFFCFF] border border-[#34656D] text-[#34656D]"
                          : activeScenario === scenarioNum
                            ? "bg-[#F1935C] text-white border-none shadow-sm"
                            : "bg-white border border-gray-200 text-gray-400 hover:border-[#F1935C]"
                        }
                      `}
                    >
                      {scenarioNum === 0 ? "BS" : `S${scenarioNum}`}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="rounded-xl border border-[#E0E0E0] bg-white">
            <div className="p-2 border-b border-[#E0E0E0]">
              <h3 className="text-sm font-medium text-[#34656d]">
                S{activeScenario} - Capacity Addition
              </h3>
            </div>
            <div className="p-2 flex flex-col gap-2">
              <CapacityConsole
                capacities={capacities}
                loading={loading}
                error={error}
                isSliderOpen={isSliderOpen}
                onAddCapacity={handleAddCapacity}
                onToggleOffer={handleToggleOffer}
                isReadOnly={activeScenario === 0}
              />

              <BatteryConsole
                batteries={batteries}
                loading={loading}
                error={error}
                isSliderOpen={isSliderOpen}
                onAddBattery={handleAddBattery}
                onToggleOffer={handleToggleOffer}
                isReadOnly={activeScenario === 0}
              />
            </div>
          </div>
        </div>

        <div className="bg-white border-t border-gray-200 mt-auto">
          <div className="flex justify-center gap-2 px-4 py-3">
            {/* Make Scenario button removed from here */}
            <button
              onClick={handleApply}
              disabled={isFinalized || applyLoading || loading || processing}
              className={`w-full h-[42px] text-base font-medium rounded-lg cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isFinalized || applyLoading || loading || processing
                ? "bg-gray-100 text-gray-400 border border-gray-200 shadow-none"
                : "text-white bg-[#F1935C] shadow-md shadow-orange-500/20 hover:bg-[#cd733f]"
                }`}
            >
              {applyLoading || loading ? "Loading..." : processing ? "Processing..." : "Apply"}
            </button>
          </div>
        </div>

        {error && (
          <div className="px-4 pb-3 bg-white">
            <p className="text-sm text-red-500 text-center">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
}
